/**
 * Data Loader Hook for Alpha Discovery Dashboard
 * Manages data fetching, loading states, and error handling
 */

import { useEffect, useCallback, useRef } from 'react'
import { useDashboardStore } from '../store/dashboardStore'
import { apiClient, ApiError } from '../services/api'
import type { StrategyEntry, RunMetadata, StatsResponse } from '../types'

// Configuration for data loading behavior
const INITIAL_LOAD_TIMEOUT = 30000 // 30 seconds for initial load
const REFRESH_INTERVAL = 60000 // Refresh every 60 seconds
const MAX_RETRIES = 2 // Reduce retries to prevent infinite loops
const RETRY_DELAY_BASE = 2000 // Increase base delay
const MAX_CONSECUTIVE_FAILURES = 5 // Circuit breaker threshold

interface UseDataLoaderOptions {
  /** Whether to automatically start loading data on mount */
  autoLoad?: boolean
  /** Whether to enable periodic refresh */
  enableRefresh?: boolean
  /** Custom refresh interval in milliseconds */
  refreshInterval?: number
  /** Maximum number of retry attempts */
  maxRetries?: number
}

interface DataLoaderState {
  isInitialLoad: boolean
  isRefreshing: boolean
  lastLoadTime: Date | null
  retryCount: number
  hasLoadedOnce: boolean
  consecutiveFailures: number
  isCircuitBroken: boolean
}

export function useDataLoader(options: UseDataLoaderOptions = {}) {
  const {
    autoLoad = true,
    enableRefresh = true,
    refreshInterval = REFRESH_INTERVAL,
    maxRetries = MAX_RETRIES
  } = options

  // Store actions and state
  const {
    setLoading,
    setError,
    setConnectionStatus,
    setLastUpdate,
    setStrategies,
    setRuns,
    isLoading,
    error,
    connectionStatus
  } = useDashboardStore()

  // Internal state for tracking load operations
  const stateRef = useRef<DataLoaderState>({
    isInitialLoad: true,
    isRefreshing: false,
    lastLoadTime: null,
    retryCount: 0,
    hasLoadedOnce: false,
    consecutiveFailures: 0,
    isCircuitBroken: false
  })

  // Refs for cleanup
  const refreshTimeoutRef = useRef<number>()
  const retryTimeoutRef = useRef<number>()
  const abortControllerRef = useRef<AbortController>()

  /**
   * Calculate exponential backoff delay
   */
  const getRetryDelay = (retryCount: number): number => {
    return RETRY_DELAY_BASE * Math.pow(2, retryCount)
  }

  /**
   * Handle errors with appropriate user messaging
   */
  const handleError = useCallback((error: Error | ApiError, context: string) => {
    console.error(`Data loading error (${context}):`, error)

    let errorMessage = 'Failed to load data'
    
    if (error instanceof ApiError) {
      if (error.statusCode === 408) {
        errorMessage = 'Request timeout - please check your connection'
      } else if (error.statusCode === 500) {
        errorMessage = 'Server error - please try again later'
      } else if (error.statusCode === 404) {
        errorMessage = 'Data not found - the database may be empty'
      } else {
        errorMessage = error.message
      }
    } else if (error.name === 'AbortError') {
      errorMessage = 'Request was cancelled'
    } else {
      errorMessage = `${context}: ${error.message}`
    }

    setError(errorMessage)
    setConnectionStatus('disconnected')
  }, [setError, setConnectionStatus])

  /**
   * Main data loading function
   */
  const loadData = useCallback(async (isRetry = false): Promise<boolean> => {
    const state = stateRef.current

    try {
      // Circuit breaker - stop retrying if too many failures
      if (state.isCircuitBroken) {
        console.log('Circuit breaker is open, not attempting load')
        return false
      }

      // Don't start a new load if one is already in progress
      if (isLoading && !isRetry) {
        return false
      }

      // Set loading state
      if (state.isInitialLoad) {
        setLoading(true)
        setConnectionStatus('connecting')
      } else {
        state.isRefreshing = true
      }

      // Clear any previous error
      setError(null)

      // Create abort controller for this request
      abortControllerRef.current?.abort()
      abortControllerRef.current = new AbortController()

      // Load all dashboard data
      console.log('Loading dashboard data...')
      const startTime = Date.now()

      const data = await apiClient.loadDashboardData()

      const loadTime = Date.now() - startTime
      console.log(`Data loaded successfully in ${loadTime}ms:`, {
        strategies: data.strategies.length,
        runs: data.runs.length,
        stats: data.stats
      })

      // Update store with fetched data
      setStrategies(data.strategies)
      setRuns(data.runs)
      setLastUpdate(new Date().toISOString())
      setConnectionStatus('connected')

      // Update internal state - RESET failure counters on success
      state.lastLoadTime = new Date()
      state.retryCount = 0
      state.consecutiveFailures = 0 // Reset failure counter
      state.isCircuitBroken = false // Reset circuit breaker
      state.hasLoadedOnce = true
      
      if (state.isInitialLoad) {
        state.isInitialLoad = false
        setLoading(false)
      }
      
      state.isRefreshing = false

      // Schedule next refresh if enabled (but only after successful load)
      if (enableRefresh && state.hasLoadedOnce) {
        refreshTimeoutRef.current = setTimeout(() => {
          loadData() // Don't call during initial load anymore
        }, refreshInterval)
      }

      return true

    } catch (error) {
      console.error('Data loading failed:', error)

      // Handle abort errors gracefully
      if (error instanceof Error && error.name === 'AbortError') {
        return false
      }

      // Update internal state and track failures
      state.isRefreshing = false
      state.consecutiveFailures++

      // Circuit breaker logic
      if (state.consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
        state.isCircuitBroken = true
        console.log('Circuit breaker activated - too many consecutive failures')
        handleError(error as Error, 'Service temporarily unavailable')
        if (state.isInitialLoad) {
          setLoading(false)
        }
        return false
      }

      // Handle retries for initial load ONLY
      if (state.isInitialLoad && state.retryCount < maxRetries) {
        state.retryCount++
        
        const retryDelay = getRetryDelay(state.retryCount)
        console.log(`Retrying data load (attempt ${state.retryCount}/${maxRetries}) in ${retryDelay}ms...`)
        
        handleError(error as Error, `Load attempt ${state.retryCount}`)
        
        retryTimeoutRef.current = setTimeout(() => {
          loadData(true)
        }, retryDelay)
        
        return false
      }

      // Final error handling - no more retries
      handleError(error as Error, state.isInitialLoad ? 'Initial load failed' : 'Refresh failed')
      
      if (state.isInitialLoad) {
        setLoading(false)
      }

      // IMPORTANT: Do not schedule another refresh on error to prevent infinite loops

      return false
    }
  }, [
    isLoading,
    setLoading,
    setError,
    setConnectionStatus,
    setLastUpdate,
    setStrategies,
    setRuns,
    enableRefresh,
    refreshInterval,
    maxRetries,
    handleError
  ])

  /**
   * Manual refresh function
   */
  const refresh = useCallback(async (): Promise<boolean> => {
    // Cancel any pending refresh
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current)
    }

    return loadData()
  }, [loadData])

  /**
   * Force reload all data (useful for manual refresh button)
   */
  const forceReload = useCallback(async (): Promise<boolean> => {
    const state = stateRef.current
    
    // Reset state for force reload
    state.isInitialLoad = true
    state.retryCount = 0
    
    return loadData()
  }, [loadData])

  /**
   * Get current loading state information
   */
  const getLoadingState = useCallback(() => {
    const state = stateRef.current
    return {
      isInitialLoad: state.isInitialLoad,
      isRefreshing: state.isRefreshing,
      lastLoadTime: state.lastLoadTime,
      retryCount: state.retryCount,
      hasLoadedOnce: state.hasLoadedOnce,
      isConnected: connectionStatus === 'connected'
    }
  }, [connectionStatus])

  // Initialize data loading
  useEffect(() => {
    if (autoLoad) {
      loadData()
    }

    // Cleanup function
    return () => {
      // Clear timeouts
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current)
      }
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current)
      }
      
      // Cancel ongoing requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [autoLoad, loadData])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Cancel any ongoing requests
      abortControllerRef.current?.abort()
    }
  }, [])

  return {
    // Actions
    loadData: () => loadData(),
    refresh,
    forceReload,
    
    // State
    getLoadingState,
    
    // Store state (for convenience)
    isLoading,
    error,
    connectionStatus,
    
    // Utilities
    clearError: () => setError(null)
  }
}

/**
 * Hook for loading specific strategy data
 */
export function useStrategyLoader(runId?: string, survivorsOnly = false) {
  const { setLoading, setError, setConnectionStatus } = useDashboardStore()
  const abortControllerRef = useRef<AbortController>()

  const loadStrategies = useCallback(async (): Promise<StrategyEntry[]> => {
    try {
      setLoading(true)
      setError(null)
      setConnectionStatus('connecting')

      // Cancel previous request
      abortControllerRef.current?.abort()
      abortControllerRef.current = new AbortController()

      const strategies = runId 
        ? await apiClient.strategies.getStrategiesByRun(runId, survivorsOnly)
        : (await apiClient.strategies.getStrategies({ 
            survivors_only: survivorsOnly,
            page_size: 1000 
          })).entries

      setConnectionStatus('connected')
      return strategies

    } catch (error) {
      console.error('Strategy loading failed:', error)
      setError(error instanceof Error ? error.message : 'Failed to load strategies')
      setConnectionStatus('disconnected')
      throw error
    } finally {
      setLoading(false)
    }
  }, [runId, survivorsOnly, setLoading, setError, setConnectionStatus])

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort()
    }
  }, [])

  return { loadStrategies }
}

export default useDataLoader